import { Amplify } from 'aws-amplify'
import {
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth'
import { KeyValueStorageInterface } from 'aws-amplify/utils'
import { createUserPoolsTokenProvider } from 'aws-amplify/adapter-core'

import { VerifyAccount } from './types/responses'
import { getApi } from './api-common'

function decodeJwtPayload(token?: string): any {
  if (!token) return {}
  try {
    const part = token.split('.')[1]
    const padLength = part.length % 4
    const padded = part + (padLength ? '='.repeat(4 - padLength) : '')
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
    // @ts-ignore Buffer may not exist in browser
    const json =
      typeof Buffer !== 'undefined'
        ? Buffer.from(base64, 'base64').toString('utf8')
        : atob(base64)
    return JSON.parse(json)
  } catch (e) {
    return {}
  }
}

const AMPLIFY_PREFIX = 'CognitoIdentityServiceProvider'

const performStorageOperation = async (
  operation: 'get' | 'set' | 'remove',
  key: string,
  value?: string,
): Promise<string | null> => {
  const platformStorage = getApi()?.storage
  const webStorage = typeof window !== 'undefined' ? window.localStorage : null

  try {
    if (operation === 'get') {
      if (platformStorage) {
        // Try synchronous storage first, then async storage
        const result =
          (await platformStorage.getItem(key)) ||
          (await (platformStorage.getItem as any)(key, undefined, true))
        if (result) return result
      }

      return webStorage?.getItem(key) || null
    }

    if (operation === 'set' && value !== undefined) {
      webStorage?.setItem(key, value)
      if (platformStorage) {
        await platformStorage.setItem(key, value)
      }
    }

    if (operation === 'remove') {
      webStorage?.removeItem(key)
      if (platformStorage) {
        await platformStorage.removeItem(key)
        await platformStorage.removeItem(key, true)
      }
    }
  } catch (e) {
    console.warn(`[Auth] Storage operation ${operation} failed for ${key}`, e)
  }
  return null
}

const tokenStorage: KeyValueStorageInterface = {
  async clear() {
    const { userPoolWebClientId } = Cognito.config
    if (!userPoolWebClientId) {
      console.warn('[Auth] Cannot clear tokens, Cognito not configured')
      return
    }

    const amplifyLastAuthUserKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.LastAuthUser`
    const username =
      (await performStorageOperation('get', amplifyLastAuthUserKey)) ||
      (await performStorageOperation(
        'get',
        `@MemoryStorage:${amplifyLastAuthUserKey}`,
      ))

    const keysToRemove = [
      amplifyLastAuthUserKey,
      ...(username
        ? [
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.accessToken`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.idToken`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.refreshToken`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.clockDrift`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.userData`,
          ]
        : []),
    ]

    await Promise.all(
      keysToRemove.flatMap((key) => [
        performStorageOperation('remove', key),
        performStorageOperation('remove', `@MemoryStorage:${key}`),
      ]),
    )
  },

  async getItem(key: string): Promise<string | null> {
    // Try to get the value with the original key
    let value = await performStorageOperation('get', key)

    // If not found, try with the @MemoryStorage: prefix (Amplify's default storage prefix)
    if (!value) {
      value = await performStorageOperation('get', `@MemoryStorage:${key}`)
    }
    return value
  },

  async removeItem(key: string): Promise<void> {
    await performStorageOperation('remove', key)
    await performStorageOperation('remove', `@MemoryStorage:${key}`)
  },

  async setItem(key: string, value: string): Promise<void> {
    await performStorageOperation('set', key, value)
  },
}

interface CognitoConfig {
  region?: string
  userPoolId: string
  userPoolWebClientId: string
}

const Cognito = {
  config: {} as CognitoConfig,
  createSession(accessToken: string, idToken: string) {
    const idPayload = decodeJwtPayload(idToken)
    return {
      getAccessToken: () => ({ getJwtToken: () => accessToken }),
      getIdToken: () => ({ getJwtToken: () => idToken, payload: idPayload }),
    }
  },

  getSession: async function (): Promise<any> {
    try {
      const session = await fetchAuthSession()
      const tokens = session.tokens
      if (tokens?.accessToken) {
        return this.createSession(
          String(tokens.accessToken),
          tokens.idToken ? String(tokens.idToken) : '',
        )
      }
    } catch (e) {
      console.warn('[Auth] fetchAuthSession failed', e)
    }

    // Fallback to stored tokens
    const { userPoolWebClientId } = Cognito.config
    if (userPoolWebClientId) {
      const customLastAuthUserKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.LastAuthUser`
      const username = await tokenStorage.getItem(customLastAuthUserKey)
      if (username) {
        const accessTokenKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.accessToken`
        const idTokenKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.idToken`
        const accessToken = await tokenStorage.getItem(accessTokenKey)
        const idToken = await tokenStorage.getItem(idTokenKey)
        if (accessToken) {
          return this.createSession(accessToken, idToken || '')
        }
      }
    }
    throw new Error('UserUnauthenticatedException')
  },

  getUser: async function () {
    try {
      const { username } = await getCurrentUser()
      const attributes = await fetchUserAttributes()
      return {
        attributes,
        username: (attributes as any)?.preferred_username || username,
      }
    } catch (e) {
      // Fallback: derive from session id token
      const session = await this.getSession().catch(() => null)
      const idToken = session?.getIdToken?.()
      const payload =
        idToken?.payload || decodeJwtPayload(idToken?.getJwtToken?.())
      const username = payload?.['cognito:username'] || payload?.email || ''
      return {
        attributes: payload || {},
        username: payload?.preferred_username || username,
      }
    }
  },

  init: (config: CognitoConfig) => {
    Cognito.config = config

    const authConfig = {
      Cognito: {
        userPoolClientId: config.userPoolWebClientId,
        userPoolId: config.userPoolId,
      },
    }

    const resourcesConfig = {
      Auth: authConfig,
    }

    const customTokenProvider = createUserPoolsTokenProvider(
      authConfig,
      tokenStorage,
    )

    Amplify.configure(resourcesConfig, {
      Auth: {
        tokenProvider: customTokenProvider,
      },
    })
  },

  loginWithToken: async function (
    username: string,
    data: VerifyAccount,
  ): Promise<void> {
    const tokens = {
      accessToken: data.token,
      idToken: data.id_token,
      refreshToken: data.refresh_token,
    }
    const userPoolWebClientId = Cognito.config?.userPoolWebClientId

    if (!userPoolWebClientId) {
      console.error(
        '[Auth] Cognito not configured. Missing userPoolWebClientId.',
      )
      return
    }

    const idTokenKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.idToken`
    const accessTokenKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.accessToken`
    const refreshTokenKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.refreshToken`
    const lastAuthUserKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.LastAuthUser`

    await Promise.all([
      tokenStorage.setItem(idTokenKey, tokens.idToken),
      tokenStorage.setItem(accessTokenKey, tokens.accessToken),
      tokenStorage.setItem(refreshTokenKey, tokens.refreshToken),
      tokenStorage.setItem(lastAuthUserKey, username),
    ])
  },

  logout: async function () {
    // Since we're using custom token-based auth (not Amplify's signIn/signOut flow),
    // we only need to clear the token storage. Calling signOut() throws an error
    // because there's no active Amplify session - tokens were manually stored via loginWithToken().
    await tokenStorage.clear()
  },
}

export default Cognito
